<template>
  <!-- A mobile number with its country: vue-tel-input set up as registration has it
       (RegisterContact.vue), labelled and errored like the design system's Input so
       it sits among DsInput fields. The country is `v-model:country` (ISO 2, e.g.
       SG) and the number `v-model:number` (national digits, no calling code). -->
  <div class="w-full">
    <label :for="id" class="flex items-center gap-1 mb-1">
      <span
        class="flex-1 text-[14px] leading-[20px] font-semibold text-[color:var(--hogc-field-label-color,#191817)]"
        >{{ label }}</span
      >
    </label>
    <vue-tel-input
      :class="[
        'phone-country-input bg-white border border-solid rounded-[8px] min-h-[56px]',
        error ? 'border-[#EC4E20]' : 'border-[color:var(--hogc-field-border-color,#C3C2C1)]',
      ]"
      :modelValue="text"
      :default-country="country || DEFAULT_COUNTRY"
      :auto-default-country="false"
      :preferred-countries="[DEFAULT_COUNTRY]"
      :dropdown-options="dropdownOptions"
      :input-options="inputOptions"
      @update:modelValue="text = $event"
      @on-input="onInput"
      @country-changed="onCountryChanged"
      @blur="$emit('blur')"
    />
    <p v-if="error" class="mt-1 text-[14px] leading-[20px] tracking-[-0.14px] text-[#EC4E20]">
      {{ error }}
    </p>
  </div>
</template>

<script>
const DEFAULT_COUNTRY = 'SG';

let nextId = 0;

export default {
  name: 'PhoneCountryInput',

  props: {
    label: { type: String, default: '' },
    // ISO 2 country code; Singapore when not given.
    country: { type: String, default: DEFAULT_COUNTRY },
    // The national number, digits only.
    number: { type: String, default: '' },
    error: { type: String, default: '' },
    name: { type: String, default: 'mobile' },
  },

  emits: ['update:country', 'update:number', 'validate', 'blur'],

  data() {
    nextId += 1;
    const id = `phone-country-input-${nextId}`;
    return {
      DEFAULT_COUNTRY,
      id,
      // What the field shows, as typed (vue-tel-input formats it). Only a number
      // changed from outside replaces it, never the echo of what was emitted, so
      // the field does not jump while the member types.
      text: this.number,
      emittedNumber: this.number,
      dropdownOptions: {
        showDialCodeInList: true,
        showDialCodeInSelection: true,
        showFlags: true,
        showSearchBox: true,
      },
      inputOptions: { id, name: this.name, placeholder: '', type: 'tel' },
    };
  },

  watch: {
    // The echo can come back without its trunk 0 (`0` typed for Malaysia is no
    // number yet), which is still the member's own typing.
    number(value) {
      if (value === this.emittedNumber || value === this.emittedNumber.replace(/^0/, '')) return;
      this.emittedNumber = value;
      this.text = value;
    },
  },

  methods: {
    // vue-tel-input reads the national number once it can parse what is typed;
    // until then (a digit or two) the digits typed stand in for it.
    onInput(typed, phoneObject = {}) {
      const national = phoneObject.nationalNumber
        ? String(phoneObject.nationalNumber)
        : String(typed ?? '').replace(/\D/g, '');
      this.emittedNumber = national;
      this.$emit('update:number', national);
      this.$emit('validate', Boolean(phoneObject.valid));
    },

    onCountryChanged(country) {
      if (country?.iso2) this.$emit('update:country', String(country.iso2).toUpperCase());
    },
  },
};
</script>

<style scoped>
/* main.scss gives every vue-tel-input a white 6px-radius input for registration;
   here the border is the wrapper's, as on a DsInput. */
.phone-country-input :deep(.vti__input) {
  border-radius: 8px;
  font-size: 16px;
  line-height: 24px;
}
</style>
